import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import {
  Dialog,
  Box,
  Typography,
  Button,
  DialogActions,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import logo from "../../assets/xpdlogo.png";
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from "jspdf";

const TransactionDetailsDialog = ({ open, onClose, transactionId }) => {
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      if (!transactionId) return;
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`/api/api/v1/getTransactionDetails${transactionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTransactionDetails(response.data);
      } catch (error) {
        console.error('Error fetching transaction details:', error);
        setError('Failed to load transaction details');
      } finally {
        setLoading(false);
      }
    };
    fetchTransactionDetails();
  }, [transactionId]);

  const handleDownloadPDF = () => {
    if (!transactionDetails) return;

    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    const generatePDF = async () => {
      try {
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true
        });

        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        let yPos = 20;

        const addHeader = async () => {
          try {
            const logoImg = await loadImage(logo);
            const logoHeight = 25;
            const logoWidth = (logoImg.width * logoHeight) / logoImg.height;
            const xPos = (pageWidth - logoWidth) / 2;

            const canvas = document.createElement('canvas');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(logoImg, 0, 0);
            const logoData = canvas.toDataURL('image/png', 1.0);

            doc.addImage(logoData, 'PNG', xPos, yPos - 15, logoWidth, logoHeight, '', 'FAST');
            yPos += 30;
          } catch (error) {
            console.error('Error adding header:', error);
          }
        };

        const checkAndAddNewPage = async () => {
          if (yPos > pageHeight - 60) {
            doc.addPage();
            yPos = 20;
            await addHeader();
            return true;
          }
          return false;
        };

        await addHeader();
        doc.setFont("helvetica");

        // Transaction Receipt number
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(`Transaction Receipt #${transactionDetails.transaction_id}`, 20, yPos);
        yPos += 15;

        // Date & Status section
        await checkAndAddNewPage();
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text("DATE & TIME", 20, yPos);

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        const date = new Date(transactionDetails.timestamp).toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        doc.text(date, 20, yPos + 4);

        // Status
        const status = transactionDetails.status === "0x1" ? "Completed" : "Processing";
        const statusColor = transactionDetails.status === "0x1" ? [25, 135, 84] : [255, 193, 7];
        doc.setTextColor(...statusColor);
        doc.text(status, 170, yPos + 4, { align: 'right' });
        yPos += 15;

        // Transaction Details Section
        await checkAndAddNewPage();
        doc.setFontSize(12);
        doc.setTextColor(84, 42, 116);
        doc.text("Transaction Details", pageWidth / 2, yPos, { align: 'center' });
        yPos += 12;

        const details = [
          { label: "Account Title", value: transactionDetails.account_title },
          { label: "Transaction ID", value: transactionDetails.transaction_id },
          { label: "Wallet Address", value: transactionDetails.wallet_address },
          { label: "Transaction Type", value: transactionDetails.transaction_type },
          { label: "Block Hash", value: transactionDetails.block_hash },
          { label: "Block Number", value: transactionDetails.block_number },
          { label: "Transaction Hash", value: transactionDetails.transaction_hash },
          { label: "Amount", value: `${transactionDetails.amount.replace(/[$£€¥]/g, '')} ${transactionDetails.coin}` },
          { label: "Network", value: transactionDetails.network }
        ];

        for (const detail of details) {
          await checkAndAddNewPage();
          doc.setFont("helvetica", "bold");
          doc.setTextColor(84, 42, 116);
          doc.setFontSize(9);
          doc.text(detail.label, 20, yPos);

          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(9);

          const maxWidth = 120;
          const valueStr = detail.value?.toString() || "N/A";
          const splitValue = doc.splitTextToSize(valueStr, maxWidth);

          if (yPos + (splitValue.length * 4) > pageHeight - 60) {
            await checkAndAddNewPage();
          }

          doc.text(splitValue, 70, yPos);
          yPos += Math.max(splitValue.length * 4, 6);
          doc.setDrawColor(224, 224, 224);
          doc.line(20, yPos - 2, 190, yPos - 2);
          yPos += 4;
        }

        doc.setProperties({
          title: `Transaction Receipt ${transactionDetails.transaction_id}`,
          subject: 'Transaction Receipt',
          creator: 'Your Application Name',
          author: 'Your Company Name',
          keywords: 'transaction, receipt, blockchain',
          producer: 'jsPDF'
        });

        doc.save(`transaction-receipt-${transactionDetails.transaction_hash.substring(0, 8)}.pdf`);
      } catch (error) {
        console.error('Error generating PDF:', error);
      }
    };

    generatePDF();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box sx={{ p: isMobile ? 2 : 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <Box sx={{ p: isMobile ? 2 : 4 }}>
          <Typography color="error" align="center">{error}</Typography>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          p: { xs: 2, sm: 3 },
          m: isMobile ? 1 : undefined,
          width: isMobile ? 'calc(100% - 32px)' : undefined,
          overflowY: 'auto'
          // width: isMobile ? 'calc(100% - 32px)' : undefined,
        }
      }}
    >
      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <img
            src={logo}
            alt="XPD Logo"
            style={{
              height: isMobile ? '40px' : '60px',
              width: 'auto'
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: 'grey.500' }}>
            Transaction Receipt #{transactionDetails.transaction_id}
          </Typography>
          <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 0.5 }}>
            DATE & TIME
          </Typography>
          <Typography variant="body1">
            {transactionDetails?.timestamp ? (
              new Date(transactionDetails.timestamp).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })
            ) : 'N/A'}
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mt: isMobile ? 0 : -4,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center'
        }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              px: 2,
              py: 0.5,
              borderRadius: '50px',
              ...(transactionDetails?.status === "0x1"
                ? { color: '#198754' }
                : { color: '#ffc107' }
              )
            }}
          >
            {transactionDetails?.status === "0x1" ? "Completed" : "Processing"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#542a74',
            mb: 1,
            pb: 0.5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          Transaction Details
        </Typography>

        <Box sx={{ mt: 2 }}>
          {transactionDetails && (
            <>
              <DetailRow label="Account Title" value={transactionDetails.account_title} isMobile={isMobile} />
              <DetailRow label="Transaction ID" value={transactionDetails.transaction_id} isMobile={isMobile} />
              <DetailRow label="Wallet Address" value={transactionDetails.wallet_address} isMobile={isMobile} />
              <DetailRow label="Transaction Type" value={transactionDetails.transaction_type} isMobile={isMobile} />
              <DetailRow label="Block Hash" value={transactionDetails.block_hash} isMobile={isMobile} />
              <DetailRow label="Block Number" value={transactionDetails.block_number} isMobile={isMobile} />
              <DetailRow label="Transaction Hash" value={transactionDetails.transaction_hash} isMobile={isMobile} />
              <DetailRow
                label="Amount"
                value={`${transactionDetails.amount.replace(/[$£€¥]/g, '')} ${transactionDetails.coin}`}
                isMobile={isMobile}
              />
              <DetailRow label="Network" value={transactionDetails.network} isMobile={isMobile} />
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <DialogActions sx={{ 
          mt: 3, 
          px: 0, 
          pb: 0,
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto',
          gap: isMobile ? 1 : 2
        }}>
          <Button
            onClick={onClose}
            sx={{ 
              color: 'grey.500',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadPDF}
            startIcon={<DownloadIcon />}
            sx={{ 
              bgcolor: '#542a74', 
              '&:hover': { bgcolor: '#141b5f' },
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Download Receipt
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const DetailRow = ({ label, value, isMobile }) => (
  <Box
    sx={{
      py: 1.5,
      borderBottom: '1px solid #e0e0e0',
      '&:last-child': { borderBottom: 'none' }
    }}
  >
    <Grid container>
      <Grid item xs={12} sm={3}>
        <Typography
          sx={{
            color: '#542a74',
            fontWeight: 600,
            textTransform: 'capitalize',
            fontSize: isMobile ? '0.875rem' : undefined
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={9}>
        <Typography
          sx={{
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            fontSize: isMobile ? '0.875rem' : undefined
          }}
        >
          {value?.toString() || "N/A"}
        </Typography>
      </Grid>
    </Grid>
  </Box>
);

export default TransactionDetailsDialog;
